import { makeAutoObservable, runInAction, toJS } from 'mobx'

import { restClient } from '@/Core'
import { GetCollectionRequestParamsTask } from '@/Core/Tasks/Resources/GetCollectionRequestParamsTask'

/** Поле, за яким фільтрує рядок, — у всіх реєстрах проєкту воно одне. */
export const STATUS_FIELD = 'status'

/** Затримка, за яку сплеск перезавантажень списку зливається в один перерахунок. */
const DEBOUNCE_MS = 150

type Params = Record<string, unknown>
type Transformer = (params: Params, listContext: unknown) => Params

/**
 * Мінімум, який рядок знає про ядровий `ListStore`.
 *
 * Описано вузько навмисно: ядровий стор написаний на JS-подібному `any`, і
 * повний його контракт тут не потрібен — лише те, з чого будується запит.
 */
export interface StatusListContextLike {
    restUrl?: string
    filter?: Params
    prefilter?: Params
    search?: string
    props?: { searchParamsTransformer?: Transformer }
}

/** Відповідь `GET /services/<колекція>/counters`. */
interface CountersResponse {
    total?: number
    byStatus?: Record<string, number>
}

/**
 * Лічильники рядка статусів.
 *
 * Приходять **одним запитом** — `GET <restUrl>/counters`, — з тими самими
 * `filters` і `q`, що й таблиця, зібраними **тим самим трансформером**
 * (`searchParamsTransformer` списку). Бек рахує їх тим самим екшеном, що й
 * список (`CountsByStatusTrait`), тож число на плашці й таблиця під нею
 * розійтися не можуть.
 *
 * ⚠️ Перша редакція робила по запиту `limit=1` на кожен статус. На цьому
 * стенді навіть такий запит коштує ~1 с, і вісім паралельних займали всіх
 * воркерів PHP-FPM на чотири секунди — запит самої таблиці після кліку по
 * плашці ставав у чергу за ними.
 *
 * ⚠️ Перераховуємо **після кожного завантаження списку**, а не лише на зміну
 * фільтра. Інакше плашки застигали б після переходу в картці: документ
 * погодили, він пішов в інший статус, а цифри лишилися старими.
 *
 * ⚠️ Запит іде з власним `signal`. Це не лише скасування застарілого: без
 * `signal` ядровий `restClient` на пів хвилини кешує GET-відповідь за ключем
 * запиту, і лічильник після переходу показував би стару цифру.
 */
export class StatusFilterBarStore {
    /** Скільки записів у кожному статусі за поточних інших фільтрів. */
    counts: Record<string, number> = {}

    /** Скільки записів без огляду на статус — плашка «Всі». `null` — ще невідомо. */
    total: number | null = null

    private controller: AbortController | null = null
    private timer: ReturnType<typeof setTimeout> | null = null

    constructor(private readonly listContext: StatusListContextLike) {
        makeAutoObservable<StatusFilterBarStore, 'controller' | 'timer' | 'listContext'>(this, {
            controller: false,
            timer: false,
            listContext: false,
        })
    }

    /** Перерахувати трохи згодом — сплеск завантажень дає один перерахунок. */
    scheduleRefresh(): void {
        if (this.timer !== null) clearTimeout(this.timer)

        this.timer = setTimeout(() => {
            this.timer = null
            void this.refresh()
        }, DEBOUNCE_MS)
    }

    async refresh(): Promise<void> {
        const restUrl = this.listContext.restUrl

        if (!restUrl) return

        this.controller?.abort()

        const controller = new AbortController()
        this.controller = controller

        try {
            const response = (await restClient.get(`${restUrl}/counters`, {
                searchParams: this.params() as Record<string, string | number | boolean>,
                signal: controller.signal,
            })) as unknown as CountersResponse | null

            if (controller.signal.aborted) return

            runInAction(() => {
                this.total = Number(response?.total ?? 0)
                // Статуси без записів бек не віддає — для них плашка покаже нуль
                this.counts = { ...(response?.byStatus ?? {}) }
            })
        } catch (error: unknown) {
            // Скасований запит — не помилка: його замінив свіжіший.
            // Решту мовчки ковтаємо: рядок статусів — підказка над таблицею, і
            // таблиця не повинна падати разом із ним. Лишаються останні цифри
            if ((error as Error)?.name === 'AbortError') return
        }
    }

    /**
     * Параметри запиту — ті самі фільтри й пошук, що в таблиці, без статусу.
     *
     * Статус прибираємо: плашки показують, скільки записів у **кожному**
     * статусі, і обраний не має обнуляти сусідів. Бек його однаково викидає,
     * але слати те, що буде проігноровано, — зайва загадка для того, хто
     * читатиме мережу.
     */
    private params(): Params {
        const filter: Params = {
            ...(toJS(this.listContext.filter) ?? {}),
            ...(toJS(this.listContext.prefilter) ?? {}),
        }

        delete filter[STATUS_FIELD]

        const transformer: Transformer =
            typeof this.listContext.props?.searchParamsTransformer === 'function'
                ? this.listContext.props.searchParamsTransformer
                : (GetCollectionRequestParamsTask as unknown as Transformer)

        const params = transformer(
            {
                sorter: {},
                filter,
                pagination: {},
                ...(this.listContext.search ? { search: this.listContext.search } : {}),
            },
            this.listContext
        )

        // Сторінка й сортування лічильникам ні до чого
        delete params['page']
        delete params['limit']
        delete params['sort']

        return params
    }

    dispose(): void {
        if (this.timer !== null) clearTimeout(this.timer)

        this.controller?.abort()
        this.controller = null
    }
}
